import React, {useCallback, useState, useEffect} from 'react'
import {useEvent, useUpdate} from 'Event/EventProvider'
import {useAsync} from 'lib/async'
import {api} from 'lib/url'
import Page from 'organization/Event/Page'
import {useOrganization} from 'organization/OrganizationProvider'
import {useOwnerHasPlan} from 'organization/auth/IfOwnerPlan'
import {ENTERPRISE} from 'obvio/Billing/plans'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'

export type WebhookFormData = {
  webhook_event: string
  custom_label: string
  url: string
  requires_crc: boolean
}

export type EventWebhookFormData = {
  webhook_url: string
  webhook_access_token_id: number
}

export interface WebhookSaltResponseData {
  webhook_salt: string
}

export interface WebhookTestResponseData {
  headers: string[]
  destination: Partial<Webhook>
  payload: any
  response: string
}

export interface WebhookData {
  has_webhook_crc_salt: boolean
  webhook_access_token_id: number | null
  webhook_url: string | null
}

export interface Webhook {
  id: number
  event_id: number
  webhook_event: string
  custom_label?: string
  url: string | null
  requires_crc: boolean
}

export interface ButtonWebhook {
  id: number
  data: string
}

interface WebhooksProviderContextProps {
  closeSaltOverlay: () => void
  generateSalt: () => Promise<string | void>
  removeWebhook: (id: number) => Promise<void>
  salt: string
  saveEventWebhook: (data: EventWebhookFormData) => Promise<void>
  saveWebhook: (data: WebhookFormData | FormData) => Promise<void>
  submitting: boolean
  testRequest?: Webhook
  testResponse?: WebhookTestResponseData
  testWebhook: (webhook: Webhook) => Promise<WebhookTestResponseData | void>
  webhooks: Webhook[]
  customWebhooks: Webhook[]
}

export const CUSTOM_WEBHOOK = 'custom_webhook'
export const WEBHOOK_ATTENDEE_CREATED = 'attendee.created'
export const WEBHOOK_ATTENDEE_CHECKED_IN = 'attendee.checked_in'
export const WEBHOOK_ATTENDEE_SIGNED_WAIVER = 'attendee.signed_waiver'
export const WEBHOOK_ATTENDEE_ZOOM_PARTICIPANT_JOINED =
  'attendee.zoom.participant_joined'
export const WEBHOOK_ATTENDEE_ZOOM_PARTICIPANT_LEFT =
  'attendee.zoom.participant_left'

export const AVAILABLE_WEBHOOK_EVENTS = [
  WEBHOOK_ATTENDEE_CREATED,
  WEBHOOK_ATTENDEE_CHECKED_IN,
  WEBHOOK_ATTENDEE_SIGNED_WAIVER,
  WEBHOOK_ATTENDEE_ZOOM_PARTICIPANT_JOINED,
  WEBHOOK_ATTENDEE_ZOOM_PARTICIPANT_LEFT,
  CUSTOM_WEBHOOK,
]

const WebhooksProviderContext = React.createContext<
  undefined | WebhooksProviderContextProps
>(undefined)

export default function WebhooksProvider(props: {
  children: React.ReactElement
  noLoader?: boolean
}) {
  const {noLoader} = props
  const [salt, setSalt] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [testRequest, setTestRequest] = useState<Webhook>()
  const [testResponse, setTestResponse] = useState<WebhookTestResponseData>()
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [customWebhooks, setCustomWebhooks] = useState<Webhook[]>([])
  const {loading, data} = useFetchWebhooks()

  const insert = (webhook: Webhook) =>
    setWebhooks((existing) => [...existing, webhook])

  const removeWithId = (id: number) => {
    setWebhooks((webhooks) => webhooks.filter((w) => w.id !== id))
  }

  const requestGenerateSalt = useGenerateSalt()
  const saveSalt = (data: WebhookSaltResponseData): Promise<string> => {
    setSalt(data.webhook_salt)

    return new Promise((resolve) => resolve(data.webhook_salt || ''))
  }
  const generateSalt = useCallback((): Promise<string | void> => {
    setSubmitting(true)

    return requestGenerateSalt()
      .catch((error) => {
        setSubmitting(false)
        throw error
      })
      .then(saveSalt)
      .then(() => setSubmitting(false))
  }, [requestGenerateSalt])

  const closeSaltOverlay = () => setSalt('')

  const sendEventWebhookSettings = useSendEventWebhookSettings()

  const handleEventWebhookSettings = (
    data: EventWebhookFormData,
  ): Promise<void> => {
    setSubmitting(true)

    return sendEventWebhookSettings(data)
      .catch((error) => {
        setSubmitting(false)
        throw error
      })
      .then(() => setSubmitting(false))
  }

  const requestSaveWebhook = useSaveWebhook()
  const saveWebhook = (data: WebhookFormData | FormData): Promise<void> => {
    setSubmitting(true)

    return requestSaveWebhook(data)
      .catch((error) => {
        setSubmitting(false)
        throw error
      })
      .then(insert)
      .then(() => setSubmitting(false))
  }

  const requestRemoveWebhook = useRemoveWebhook()
  const removeWebhook = (id: number): Promise<void> => {
    setSubmitting(true)

    return requestRemoveWebhook(id)
      .then(() => removeWithId(id))
      .then(() => setSubmitting(false))
  }

  const requestTestWebhook = useTestWebhook()
  const testWebhook = (
    webhook: Webhook,
  ): Promise<WebhookTestResponseData | void> => {
    setTestResponse(undefined)
    setTestRequest(webhook)

    return requestTestWebhook(webhook.id).then((data) => {
      if (data) {
        setTestResponse(data)
      }
    })
  }

  useEffect(() => {
    if (!data) {
      return
    }

    const localWebhooks = []
    const localCustomWebhooks = []

    for (const idx in data) {
      const webhook = data[idx]

      if (webhook.webhook_event === CUSTOM_WEBHOOK) {
        localCustomWebhooks.push(webhook)
      }

      localWebhooks.push(webhook)
    }

    setWebhooks(localWebhooks)
    setCustomWebhooks(localCustomWebhooks)
  }, [data])

  if (noLoader !== true && (loading || !data)) {
    return (
      <Page>
        <FullPageLoader />
      </Page>
    )
  }

  return (
    <WebhooksProviderContext.Provider
      value={{
        closeSaltOverlay,
        customWebhooks,
        generateSalt,
        removeWebhook,
        salt,
        saveEventWebhook: handleEventWebhookSettings,
        saveWebhook,
        submitting,
        testRequest,
        testResponse,
        testWebhook,
        webhooks,
      }}
    >
      {props.children}
    </WebhooksProviderContext.Provider>
  )
}

export function PlanWebhooksProvider(props: {children: JSX.Element}) {
  const planHasWebhooks = useOwnerHasPlan(ENTERPRISE)
  if (planHasWebhooks) {
    return <WebhooksProvider>{props.children}</WebhooksProvider>
  }

  return props.children
}

export function useSendEventWebhookSettings() {
  const updateEvent = useUpdate()

  return (data: EventWebhookFormData) => updateEvent(data)
}

export function useSaveWebhook() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const url = api(`/events/${event.id}/webhooks`)

  return (data: WebhookFormData | FormData) => client.post<Webhook>(url, data)
}

function useRemoveWebhook() {
  const {client} = useOrganization()

  return (id: number) => client.delete<void>(api(`/webhooks/${id}`))
}

function useTestWebhook() {
  const {client} = useOrganization()

  return (id: number) =>
    client.post<WebhookTestResponseData | void>(api(`/webhooks/${id}/test`))
}

function useGenerateSalt() {
  const {client} = useOrganization()
  const {
    event: {id},
  } = useEvent()

  return useCallback(() => {
    const url = api(`/events/${id}/webhooks/salt`)

    return client.post<WebhookSaltResponseData>(url)
  }, [id, client])
}

function useFetchWebhooks() {
  const {client} = useOrganization()
  const {
    event: {slug: eventSlug},
  } = useEvent()

  const request = useCallback(() => {
    const url = api(`/events/${eventSlug}/webhooks`)

    return client.get<Webhook[]>(url)
  }, [client, eventSlug])

  return useAsync(request)
}

export function useWebhooks() {
  const context = React.useContext(WebhooksProviderContext)

  if (context === undefined) {
    throw new Error('useWebhooks must be used within a WebhooksProvider')
  }

  return context
}

export function useFireWebhook() {
  const {client, event} = useEvent()

  return (webhook: ButtonWebhook) => {
    const url = api(`/events/${event.id}/webhooks/${webhook.id}`)

    // If there's no link to a webhook, we're not going to fire anything.
    if (!webhook.id) {
      return
    }

    client
      .post(url, {
        id: webhook.id,
        data: webhook.data,
      })
      .catch((_error) => {
        // Ignore errors, failing to fire a webhook should not break the app.
      })
  }
}
